import Icon from '@/components/ui/AppIcon';

interface Benefit {
  icon: string;
  title: string;
  description: string;
  metrics: string;
}

const BenefitsSection = () => {
  const benefits: Benefit[] = [
    {
      icon: 'BoltIcon',
      title: 'Lightning-Fast Response Times',
      description: 'AI-powered instant responses to common queries with intelligent routing for complex issues. Eliminate wait times and deliver immediate value to customers.',
      metrics: '85% faster than traditional support'
    },
    {
      icon: 'CurrencyDollarIcon',
      title: 'Dramatic Cost Reduction',
      description: 'Automate 70% of routine inquiries while maintaining quality. Scale support operations without proportional cost increases.',
      metrics: '60% reduction in support costs'
    },
    {
      icon: 'ClockIcon',
      title: '24/7 Global Availability',
      description: 'Never miss a customer inquiry with round-the-clock AI support. Provide consistent service across all time zones and languages.',
      metrics: 'Zero downtime, unlimited capacity'
    },
    {
      icon: 'ChartBarIcon',
      title: 'Continuous Learning & Improvement',
      description: 'AI systems that learn from every interaction, constantly improving accuracy and effectiveness. Leverage historical data for predictive support.',
      metrics: '15% monthly accuracy improvement'
    },
    {
      icon: 'UserGroupIcon',
      title: 'Enhanced Agent Productivity',
      description: 'Free your team from repetitive tasks to focus on high-value customer relationships. AI handles routine, agents handle complex.',
      metrics: '3x increase in agent efficiency'
    },
    {
      icon: 'ShieldCheckIcon',
      title: 'Consistent Quality Standards',
      description: 'Eliminate variability in support quality with standardized AI responses. Ensure brand voice consistency across all customer touchpoints.',
      metrics: '98% customer satisfaction rate'
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
            <Icon name="SparklesIcon" size={20} variant="solid" />
            <span className="text-sm font-heading font-semibold">Measurable Impact</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
            Transform Support Operations with AI-Driven Excellence
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Our AI support systems deliver quantifiable improvements across every metric that matters to your business and customers.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-card rounded-xl p-8 border border-border hover:border-accent/50 hover:shadow-elevated transition-all duration-300 group"
            >
              <div className="w-14 h-14 bg-accent/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors duration-300">
                <Icon name={benefit.icon as any} size={28} className="text-accent" />
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                {benefit.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {benefit.description}
              </p>
              <div className="pt-4 border-t border-border">
                <div className="flex items-center space-x-2">
                  <Icon name="ChartBarIcon" size={16} className="text-success" />
                  <span className="text-sm font-heading font-semibold text-success">
                    {benefit.metrics}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-lg text-muted-foreground mb-6">
            Ready to see these benefits in your organization?
          </p>
          <button className="px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-0.5 inline-flex items-center space-x-2">
            <span>Schedule Strategy Session</span>
            <Icon name="ArrowRightIcon" size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;